import { UserTradingSystemCredentials } from '@monorepo/types';
import { AccountStore } from '@hub/stores/data/account/account-store';
import UserStore from '@hub/stores/data/user/user-store';
import { UseNovaTraderLoginParams } from '../types';

/**
 * Parameters for fetching trading system credentials
 */
export interface FetchTradingCredentialsParams
  extends UseNovaTraderLoginParams {
  contestAccountStore: {
    getCredentials: (tsAccountId: string) => Promise<{
      login: string;
      password: string;
    }>;
  };
  accountStore: AccountStore;
  userStore: UserStore;
}

/**
 * Shared query function for fetching trading system credentials
 * Used by both useNovaTraderLogin and usePrefetchCredentials
 *
 * @param params - Parameters including stores and user/account info
 * @returns UserTradingSystemCredentials or null if not found
 */
export const fetchTradingCredentials = async ({
  userId,
  tradingSystem,
  accountId,
  contest,
  contestAccountStore,
  accountStore,
  userStore,
}: FetchTradingCredentialsParams): Promise<UserTradingSystemCredentials | null> => {
  try {
    if (contest && accountId) {
      // Contest account credentials
      const { login, password } =
        await contestAccountStore.getCredentials(accountId);
      return {
        login,
        password,
        tradingSystem,
        externalId: userId,
      } as UserTradingSystemCredentials;
    } else {
      // Regular account credentials
      const tsUsers = userStore.currentUser.tsUsers || [];
      const currentUser = tsUsers.find((user: UserTradingSystemCredentials) => {
        return (
          user.tradingSystem === tradingSystem &&
          user.externalId.toString() === userId.toString()
        );
      });

      if (currentUser) {
        const decryptedPassword = await accountStore.decryptPassword(
          currentUser.login,
          currentUser.tradingSystem,
        );

        return {
          ...currentUser,
          password: decryptedPassword || '',
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch trading system credentials:', error);
    return null;
  }
};
